import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import useRoleEditService from "@/pages/settings/roles/edit/role-edit-service.ts";
import { RuleEditModal } from "@/pages/settings/roles/edit/widgets/RuleEditModal.tsx";
import { useRuleGroupsPreview } from "@/queries/use-rules.query.ts";
import { RuleRegistryPlace, RuleRegistryPlaceKey } from "@/types/rule-whitelists.ts";
import {
  detectGroupingPreset,
  flattenRuleGroups,
  groupRulesByPreset,
  RuleGroupingPreset,
  ruleGroupingPresetOptions,
} from "@/components/apply-to/rule-grouping.ts";
import { Rule, RuleConditionsItems, RuleGroup } from "@/types/role.types.ts";
import { RuleApplySignType, RuleConditionType, RuleShort } from "@/types/rule.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  Card,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Text,
  SelectItem,
  Skeleton,
  Stack,
  Tag,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { Fragment, useState } from "react";
import {
  Control,
  FieldPath,
  FieldValues,
  PathValue,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

type RuleGroupsEditorProps<TFieldValues extends FieldValues = FieldValues> = {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  fieldName: FieldPath<TFieldValues>;
  place: RuleRegistryPlaceKey;
  conditionTypes?: SelectItem[];
  singleSelectConditionTypes?: RuleConditionType[];
  additionalHiddenRules?: RuleShort[];
  maxCardsPerRow?: number;
  isAddRuleButtonFullWidth?: boolean;
  assignToGroups?: RuleGroup[];
  countEmployees?: boolean;
  variant?: "cards" | "list";
  error?: string;
};

export const RuleGroupsEditor = <TFieldValues extends FieldValues = FieldValues>({
  control,
  setValue,
  fieldName,
  place,
  conditionTypes,
  singleSelectConditionTypes,
  additionalHiddenRules = [],
  maxCardsPerRow = 3,
  isAddRuleButtonFullWidth = false,
  assignToGroups,
  countEmployees = true,
  variant = "cards",
  error,
}: RuleGroupsEditorProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { getApplyCardTitle, getAssignCardDescription } = useRoleEditService();

  const watched = useWatch({ control, name: fieldName });
  const groups: RuleGroup[] = Array.isArray(watched) ? (watched as RuleGroup[]) : [];

  const [preset, setPreset] = useState<RuleGroupingPreset>(() => detectGroupingPreset(groups));
  const [isPresetDetected, setIsPresetDetected] = useState(groups.length > 0);

  if (!isPresetDetected && groups.length > 0) {
    setIsPresetDetected(true);
    setPreset(detectGroupingPreset(groups));
  }

  const writeGroups = (nextGroups: RuleGroup[]) => {
    setValue(fieldName, nextGroups as PathValue<TFieldValues, typeof fieldName>, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handlePresetChange = (nextPreset: RuleGroupingPreset) => {
    setPreset(nextPreset);

    if (nextPreset === "custom_groups") {
      if (groups.length === 0) writeGroups([{ rules: [] }]);
      return;
    }

    writeGroups(groupRulesByPreset(flattenRuleGroups(groups), nextPreset));
  };

  const flatRules = flattenRuleGroups(groups);
  const hideSummary = variant === "list" && flatRules.length === 1;

  const openFlatRule = (rule?: Rule, index?: number) => {
    openModal(RuleEditModal, {
      rule,
      conditionTypes: conditionTypes ?? RuleConditionsItems(),
      singleSelectConditionTypes,
      onModified(modified: Rule) {
        const next = [...flatRules];
        if (index !== undefined) next[index] = modified;
        else next.push(modified);
        writeGroups(groupRulesByPreset(next, preset));
      },
      onDelete() {
        if (index === undefined) return;
        writeGroups(
          groupRulesByPreset(
            flatRules.filter((_, i) => i !== index),
            preset
          )
        );
      },
    });
  };

  const openGroupRule = (groupIndex: number, rule?: Rule, ruleIndex?: number) => {
    openModal(RuleEditModal, {
      rule,
      conditionTypes: conditionTypes ?? RuleConditionsItems(),
      singleSelectConditionTypes,
      onModified(modified: Rule) {
        writeGroups(
          groups.map((group, gi) => {
            if (gi !== groupIndex) return group;
            const rules = [...group.rules];
            if (ruleIndex !== undefined) rules[ruleIndex] = modified;
            else rules.push(modified);
            return { ...group, rules };
          })
        );
      },
      onDelete() {
        if (ruleIndex === undefined) return;
        writeGroups(
          groups.map((group, gi) =>
            gi === groupIndex
              ? { ...group, rules: group.rules.filter((_, i) => i !== ruleIndex) }
              : group
          )
        );
      },
    });
  };

  const addGroup = () => writeGroups([...groups, { rules: [] }]);
  const removeGroup = (groupIndex: number) => {
    const next = groups.filter((_, i) => i !== groupIndex);
    writeGroups(next.length > 0 ? next : [{ rules: [] }]);
  };

  const { employeeIds, funnel, total, isLoading, isError, hasRules, refetch } =
    useRuleGroupsPreview(groups, place, countEmployees, assignToGroups, additionalHiddenRules);
  const getRuleCount = (groupIndex: number, ruleIndex: number) => {
    if (isLoading || isError) return undefined;
    const group = funnel?.groups[groupIndex];
    return [...(group?.include_steps ?? []), ...(group?.exclude_steps ?? [])].find(
      (step) => step.rule_index === ruleIndex + additionalHiddenRules.length
    )?.employees_count;
  };
  const flatRuleLocations = groups.flatMap((group, groupIndex) =>
    group.rules.map((_rule, ruleIndex) => ({ groupIndex, ruleIndex }))
  );
  const activeEmployeePlaces: RuleRegistryPlaceKey[] = [
    RuleRegistryPlace.performanceReviewParticipants,
    RuleRegistryPlace.surveyParticipants,
    RuleRegistryPlace.workflowTaskAssignTo,
    RuleRegistryPlace.workflowTaskWatchers,
    RuleRegistryPlace.workflowMeetingAttendees,
    RuleRegistryPlace.eventAttendees,
    RuleRegistryPlace.recognitionGiftRecipients,
  ];

  const renderCount = (rule: Rule, count: number | undefined) => {
    if (!countEmployees) return undefined;
    if (isLoading) return <Skeleton className="h-6 w-20 rounded-xs" />;
    if (count === undefined) return undefined;

    const label = `${rule.sign === RuleApplySignType.exclude ? "−" : ""}${t("plurals:employees_count", { count })}`;

    if (variant === "list") {
      return <Text className="text-secondary text-sm">{label}</Text>;
    }

    return (
      <Tag appearance="outline" variant="secondary">
        {label}
      </Tag>
    );
  };

  const renderGroupFunnel = (groupIndex: number, inline = false) => {
    if (!countEmployees || hideSummary || groups[groupIndex]?.rules.length === 1) return null;
    if (isLoading) return <Skeleton className="mt-4 h-4 w-48 rounded-xs" />;
    const group = funnel?.groups[groupIndex];
    if (!group || isError) return null;
    const steps = [
      ...group.include_steps.map((step) => String(step.employees_count)),
      ...group.exclude_steps.map((step) => `−${step.employees_count}`),
      `${t("core:total")} ${group.employees_count}`,
    ];
    return (
      <Text
        className={twMerge(
          "block text-secondary text-sm",
          !inline && variant !== "list" && "pt-4",
          !inline && variant === "list" && preset === "custom_groups" && "pt-2"
        )}
      >
        {steps.join(" → ")}
      </Text>
    );
  };

  const renderRuleCards = (
    rules: Rule[],
    onEdit: (rule: Rule, index: number) => void,
    onAdd: () => void,
    getCount: (index: number) => number | undefined
  ) => (
    <Stack
      direction={"horizontal"}
      gap={"md"}
      className={twMerge(
        "grid grid-cols-1 gap-6",
        maxCardsPerRow >= 2 && "md:grid-cols-2",
        maxCardsPerRow >= 3 && "xl:grid-cols-3"
      )}
    >
      {rules.map((rule: Rule, index: number) => {
        const count = getCount(index);
        return (
          <Fragment key={`rule-card-${index}`}>
            {preset === "match_any" && index > 0 && (
              <Text className="col-span-full">{t("core:rule_group_or")}</Text>
            )}
            <Card
              key={`rule-card-${index}`}
              header={getApplyCardTitle(rule.condition_type)}
              caption={getAssignCardDescription(rule)}
              onClick={() => onEdit(rule, index)}
              buttonsSlot={renderCount(rule, count)}
            />
          </Fragment>
        );
      })}
      <Card
        icon={"fa-plus"}
        header={t("core:create_new_rule")}
        onClick={onAdd}
        variant={"action"}
      />
    </Stack>
  );

  const renderRuleList = (
    rules: Rule[],
    onEdit: (rule: Rule, index: number) => void,
    onAdd: () => void,
    getCount: (index: number) => number | undefined
  ) => (
    <Stack gap={"md"}>
      {rules.length > 0 && (
        <Stack>
          {rules.map((rule: Rule, index: number) => {
            const count = getCount(index);
            return (
              <Fragment key={`rule-list-${index}`}>
                {preset === "match_any" && index > 0 && (
                  <Text className="text-secondary">{t("core:rule_group_or")}</Text>
                )}
                <ListItem
                  className={twMerge(
                    index === rules.length - 1 || preset === "match_any" ? "border-0" : ""
                  )}
                  key={`rule-list-${index}`}
                  title={getApplyCardTitle(rule.condition_type)}
                  caption={getAssignCardDescription(rule)}
                  onClick={() => onEdit(rule, index)}
                  valueSlot={renderCount(rule, count)}
                />
              </Fragment>
            );
          })}
        </Stack>
      )}
      <Button
        size={"sm"}
        variant={"secondary"}
        className={isAddRuleButtonFullWidth ? "w-full" : "self-start"}
        onClick={onAdd}
      >
        {t("core:create_new_rule")}
      </Button>
    </Stack>
  );

  const renderRules = (
    rules: Rule[],
    onEdit: (rule: Rule, index: number) => void,
    onAdd: () => void,
    getCount: (index: number) => number | undefined
  ) =>
    variant === "list"
      ? renderRuleList(rules, onEdit, onAdd, getCount)
      : renderRuleCards(rules, onEdit, onAdd, getCount);

  return (
    <Stack gap={"md"} className={twMerge(variant === "list" && "p-4 bg-dark/2 rounded-sm")}>
      {countEmployees && activeEmployeePlaces.includes(place) && (
        <Text className="text-secondary">{t("core:rule_group_base_active")}</Text>
      )}
      {countEmployees && additionalHiddenRules.length > 0 && (
        <Text className="text-secondary">{t("core:rule_group_hidden_rules_note")}</Text>
      )}
      <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
        <DropdownMenu
          trigger={
            <Button
              size={variant === "list" ? "sm" : "md"}
              variant={"primary"}
              rightIcon={<FontAwesomeIcon icon={"fa-light fa-chevron-down"} />}
            >
              {ruleGroupingPresetOptions().find((item) => item.id === preset)?.title}
            </Button>
          }
        >
          {ruleGroupingPresetOptions().map((item) => (
            <DropdownMenuItem
              key={item.id}
              title={item.title}
              isSelected={item.id === preset}
              onClick={() => handlePresetChange(item.id as RuleGroupingPreset)}
            />
          ))}
        </DropdownMenu>
        {preset === "custom_groups" && (
          <Button size={"md"} variant={"secondary"} onClick={addGroup}>
            {t("core:rule_group_add_group")}
          </Button>
        )}
      </Stack>

      {preset === "custom_groups" ? (
        <Stack gap={"md"}>
          {groups.map((group, groupIndex) => (
            <Fragment key={`rule-group-${groupIndex}`}>
              {groupIndex > 0 && <Text>{t("core:rule_group_or")}</Text>}
              <Box
                className={twMerge(
                  "bg-dark/5 rounded-xs p-4 group",
                  variant === "list" && "bg-light"
                )}
              >
                <Stack
                  direction={"horizontal"}
                  justify={"between"}
                  items={"start"}
                  className={"pb-4"}
                >
                  <Title
                    header={t("core:rule_group_title", { number: groupIndex + 1 })}
                    caption={variant === "list" ? undefined : t("core:rule_group_caption")}
                    actionIcon={
                      variant === "list" ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <FontAwesomeIcon icon="fa-light fa-circle-info opacity-0 group-hover:opacity-100 transition" />
                          </TooltipTrigger>
                          <TooltipContent>{t("core:rule_group_caption")}</TooltipContent>
                        </Tooltip>
                      ) : undefined
                    }
                    size={"sm"}
                  />
                  {(groups.length > 1 || group.rules.length > 0) &&
                    (variant === "list" ? (
                      <ButtonIcon
                        variant="plain"
                        size="sm"
                        icon={
                          <FontAwesomeIcon icon="fa-light fa-trash  opacity-0 group-hover:opacity-100 transition" />
                        }
                        aria-label={t("common:delete")}
                        onClick={() => removeGroup(groupIndex)}
                      />
                    ) : (
                      <DropdownMenu>
                        <DropdownMenuItem
                          title={t("common:delete")}
                          onClick={() => removeGroup(groupIndex)}
                          variant={"menuDanger"}
                        />
                      </DropdownMenu>
                    ))}
                </Stack>
                {renderRules(
                  group.rules,
                  (rule, ruleIndex) => openGroupRule(groupIndex, rule, ruleIndex),
                  () => openGroupRule(groupIndex),
                  (ruleIndex) => getRuleCount(groupIndex, ruleIndex)
                )}
                {renderGroupFunnel(groupIndex)}
              </Box>
            </Fragment>
          ))}
        </Stack>
      ) : (
        renderRules(
          flatRules,
          (rule, index) => openFlatRule(rule, index),
          () => openFlatRule(),
          (index) =>
            getRuleCount(flatRuleLocations[index].groupIndex, flatRuleLocations[index].ruleIndex)
        )
      )}

      {countEmployees && hasRules && (
        <Stack
          direction={"horizontal"}
          gap={"md"}
          items={"center"}
          justify={"between"}
          className={variant === "list" ? "pt-0" : "pt-4"}
        >
          {isLoading ? (
            <Skeleton className="h-6 w-64 rounded-xs" />
          ) : isError ? (
            <Stack gap="sm">
              <Text className="text-danger">{t("core:rule_group_preview_error")}</Text>
              <Button variant="secondary" onClick={() => void refetch()}>
                {t("common:retry")}
              </Button>
            </Stack>
          ) : total !== undefined ? (
            <Stack gap="sm">
              <Stack direction="horizontal" gap="md" items="center">
                <Button
                  size={variant === "list" ? "sm" : "md"}
                  disabled={employeeIds.length === 0}
                  onClick={() => {
                    openModal(PreviewEmployeesModal, {
                      filter: {
                        page_number: 1,
                        employee_ids: employeeIds,
                      },
                      addColumns: [],
                      modalTitle: t("core:employees"),
                    });
                  }}
                >
                  {funnel && groups.length > 1
                    ? t("plurals:rule_group_union", { count: total })
                    : `${t("core:total")} ${t("plurals:employees_count", { count: total })}`}
                </Button>
                {preset === "match_all" && renderGroupFunnel(0, true)}
                {funnel && groups.length > 1 && (
                  <Text className="text-secondary text-sm">
                    {t("plurals:rule_group_overlap", {
                      count: funnel.multiple_groups_employees_count,
                    })}
                  </Text>
                )}
              </Stack>
              {funnel && funnel.excluded_included_via_other_groups_count > 0 && (
                <Stack direction="horizontal" gap="xs" items="baseline">
                  <FontAwesomeIcon
                    icon="fa-light fa-triangle-exclamation"
                    className="text-warning"
                  />
                  <Text>
                    {t("plurals:rule_group_reincluded", {
                      count: funnel.excluded_included_via_other_groups_count,
                    })}
                  </Text>
                </Stack>
              )}
              {assignToGroups !== undefined && (
                <Text className="text-secondary">{t("core:rule_group_role_preview_note")}</Text>
              )}
              {employeeIds.length < total && (
                <Text className="text-secondary">{t("core:rule_group_visible_only")}</Text>
              )}
            </Stack>
          ) : null}
        </Stack>
      )}

      {error && (
        <Box className={"pt-2 pb-4"}>
          <span className={"text-danger"}>{error}</span>
        </Box>
      )}
    </Stack>
  );
};
